import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { AlertCircle, Trash2, Save, X, Check } from "lucide-react";
import { VersionDiffViewer } from "./VersionDiffViewer";
import type { CauseItem } from "./useCausesManager";

interface CauseEditFormProps {
  editingCause: Partial<CauseItem>;
  setEditingCause: React.Dispatch<React.SetStateAction<Partial<CauseItem> | null>>;
  onSave: () => void;
  onCancel: () => void;
  activeDraft?: any;
  onSaveDraft?: () => void;
  onDiscardDraft?: () => void;
  liveCause?: any;
}

interface CauseFormFieldsProps {
  editingCause: Partial<CauseItem>;
  setEditingCause: React.Dispatch<React.SetStateAction<Partial<CauseItem> | null>>;
}

export function CauseFormFields({ editingCause, setEditingCause }: CauseFormFieldsProps) {
  return (
    <tbody className="divide-y divide-border/40 text-left">
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Title</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            value={editingCause.title_en || ""}
            onChange={(e) => setEditingCause((p) => ({ ...p!, title_en: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="e.g. Open Source Contributor"
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            value={editingCause.title_tr || ""}
            onChange={(e) => setEditingCause((p) => ({ ...p!, title_tr: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="örn. Açık Kaynak Katkıcısı"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Description</td>
        <td className="px-3 py-2 border-r border-border/40">
          <Textarea
            value={editingCause.desc_en || ""}
            onChange={(e) => setEditingCause((p) => ({ ...p!, desc_en: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="e.g. Building and maintaining core tools."
          />
        </td>
        <td className="px-3 py-2">
          <Textarea
            value={editingCause.desc_tr || ""}
            onChange={(e) => setEditingCause((p) => ({ ...p!, desc_tr: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
            placeholder="örn. Çekirdek araçlar inşa ediyor ve sürdürüyorum."
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Lucide Icon Name</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            placeholder="e.g. ShieldCheck, Code2, Sparkles, Heart"
            value={editingCause.icon || ""}
            onChange={(e) => setEditingCause((p) => ({ ...p!, icon: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
      <tr className="hover:bg-muted/5 transition-colors">
        <td className="px-4 py-3 font-medium border-r border-border/40 align-middle">Tailwind Color Styles</td>
        <td colSpan={2} className="px-3 py-2">
          <Textarea
            placeholder="e.g. text-brand-primary bg-brand-primary/10"
            value={editingCause.color || ""}
            onChange={(e) => setEditingCause((p) => ({ ...p!, color: e.target.value }))}
            className="w-full bg-background/50 border-border/30 focus-visible:ring-1 focus-visible:ring-brand-primary h-9 min-h-[36px] resize-y py-1.5 text-xs"
          />
        </td>
      </tr>
    </tbody>
  );
}

export function CauseEditForm({
  editingCause,
  setEditingCause,
  onSave,
  onCancel,
  activeDraft,
  onSaveDraft,
  onDiscardDraft,
  liveCause,
}: CauseEditFormProps) {
  const diffFields = [
    { key: "title_en", label: "English Title" },
    { key: "title_tr", label: "Turkish Title" },
    { key: "desc_en", label: "English Description", isLongText: true },
    { key: "desc_tr", label: "Turkish Description", isLongText: true },
    { key: "icon", label: "Icon Name" },
    { key: "color", label: "Color Styling" },
  ];

  return (
    <div className="p-5 border border-border/60 rounded-2xl bg-card/65 space-y-4 max-w-3xl text-left">
      <h5 className="font-bold text-base text-brand-primary">
        {editingCause.id ? "Edit Value Card" : "Add Value Card"}
      </h5>

      <div className="flex flex-wrap justify-between items-center bg-muted/15 border border-border/50 rounded-xl p-3 gap-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground font-semibold">
          <AlertCircle className="h-4.5 w-4.5 text-amber-500" />
          <span>
            {activeDraft
              ? "⚠️ Staged Staging Draft has unpublished modifications."
              : "✅ Working copy is synced to database."}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {activeDraft && onDiscardDraft && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onDiscardDraft}
              className="text-destructive border-destructive/20 hover:bg-destructive/5 font-bold h-9 rounded-lg"
            >
              <Trash2 className="h-4 w-4 mr-1.5" /> Discard Draft
            </Button>
          )}
          {onSaveDraft && (
            <Button
              type="button"
              size="sm"
              onClick={onSaveDraft}
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold h-9 rounded-lg"
            >
              <Save className="h-4 w-4 mr-1.5" /> Save as Staging Draft
            </Button>
          )}
        </div>
      </div>

      <div className="overflow-x-auto border border-border/60 rounded-xl bg-card/30">
        <table className="min-w-full divide-y divide-border/40 text-sm">
          <thead className="bg-muted/40 font-semibold text-brand-secondary">
            <tr>
              <th className="px-4 py-2.5 text-left w-1/4 border-r border-border/40 text-xs uppercase tracking-wider">
                Field
              </th>
              <th className="px-4 py-2.5 text-left w-3/8 border-r border-border/40 text-xs uppercase tracking-wider">
                English (EN)
              </th>
              <th className="px-4 py-2.5 text-left w-3/8 text-xs uppercase tracking-wider">
                Turkish (TR)
              </th>
            </tr>
          </thead>
          <CauseFormFields editingCause={editingCause} setEditingCause={setEditingCause} />
        </table>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-2" /> Cancel
        </Button>
        <Button onClick={onSave} className="bg-brand-primary text-white">
          <Check className="h-4 w-4 mr-2" /> Publish Live
        </Button>
      </div>

      {liveCause && (
        <VersionDiffViewer live={liveCause} draft={editingCause} fieldsToCompare={diffFields} />
      )}
    </div>
  );
}

export default CauseEditForm;
